export interface LegacyBillingEstimate {
  billing_model?: 'legacy'
  currency?: string
  amount_due?: number
  amount_due_decimal?: number
  subtotal?: number
  subtotal_decimal?: number
  credits_applied?: number
  credits_applied_decimal?: number
  period_start?: number
  period_end?: number
  service_estimate?: Array<{
    service?: string
    service_display?: string
    tier?: string
    tier_display?: string
    server_count?: number
    cpu_hours?: number
    ram_gb_hours?: number
    servers?: Array<{
      server_uuid?: string
      cpu_hours?: number
      ram_gb_hours?: number
      transfer_gb?: number
    }>
    amount?: number
  }>
  lines?: Array<{ id?: string; amount?: number; amount_decimal?: number; quantity?: number; lookup_key?: string; description?: string }>
  error?: string
}

export interface PackageBillingEstimate {
  billing_model: 'package'
  source: 'package_billing'
  scope: 'accepted_package_servers'
  estimate_status: 'provisional'
  currency: string
  as_of: string
  usage_as_of?: string | null
  period: { start: string; end: string }
  estimated_current_spend_minor: number
  effective_floor_minor: number
  effective_cap_minor: number
  package_estimate: Record<string, unknown>
}

export interface PendingPackageBillingEstimate extends Omit<PackageBillingEstimate, 'estimate_status' | 'estimated_current_spend_minor'> {
  estimate_status: 'pending'
  estimated_current_spend_minor: null
}

export type BillingEstimate = LegacyBillingEstimate | PackageBillingEstimate | PendingPackageBillingEstimate

export function isPendingPackageEstimate(value: BillingEstimate | null | undefined): value is PendingPackageBillingEstimate {
  return value?.billing_model === 'package' && value.source === 'package_billing'
    && value.scope === 'accepted_package_servers' && value.estimate_status === 'pending'
    && value.estimated_current_spend_minor === null
}

export function isPackageBillingEstimate(value: BillingEstimate | null | undefined): value is PackageBillingEstimate {
  return value?.billing_model === 'package'
    && value.source === 'package_billing'
    && value.scope === 'accepted_package_servers'
    && value.estimate_status === 'provisional'
    && Number.isInteger(value.estimated_current_spend_minor)
    && Number.isInteger(value.effective_floor_minor)
    && Number.isInteger(value.effective_cap_minor)
    && typeof value.currency === 'string'
    && /^[a-z]{3}$/.test(value.currency)
    && typeof value.as_of === 'string'
    && Number.isFinite(Date.parse(value.as_of))
    && typeof value.period === 'object' && value.period !== null
    && typeof value.period.start === 'string' && Number.isFinite(Date.parse(value.period.start))
    && typeof value.period.end === 'string' && Number.isFinite(Date.parse(value.period.end))
    && typeof value.package_estimate === 'object' && value.package_estimate !== null && !Array.isArray(value.package_estimate)
}

export function formatMinorCurrency(minor: number, currency: string): string {
  const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency })
  const digits = formatter.resolvedOptions().maximumFractionDigits ?? formatter.resolvedOptions().minimumFractionDigits ?? 2
  return formatter.format(minor / (10 ** digits))
}

export function estimateSpend(estimate: BillingEstimate | null | undefined): { amount: string; scope: 'package' | 'legacy' } | null {
  if (!estimate) return null
  if ((estimate as { billing_model?: string }).billing_model === 'package') {
    return isPackageBillingEstimate(estimate)
      ? { amount: formatMinorCurrency(estimate.estimated_current_spend_minor, estimate.currency), scope: 'package' }
      : null
  }
  if (estimate.billing_model !== undefined && estimate.billing_model !== 'legacy') return null
  const legacy = estimate as LegacyBillingEstimate
  const amount = typeof legacy.subtotal_decimal === 'number' ? legacy.subtotal_decimal : legacy.amount_due_decimal
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return null
  return { amount: new Intl.NumberFormat(undefined, { style: 'currency', currency: legacy.currency || 'USD' }).format(amount), scope: 'legacy' }
}
